import React, { useState, useEffect } from "react"
import { Col, Row } from "reactstrap"
import { useHistory } from "react-router-dom"
import toast from "react-hot-toast"
import { DragDropContext, Droppable } from "react-beautiful-dnd"

import eyeIcon from "../../../assets/images/icons/eye-icon.svg"
import editIcon from "../../../assets/images/icons/edit-icon.svg"
import DeleteIcon from "assets/images/icons/finance-vendor-mang-del-icon.svg"

import ActiveInactiveSwitch from "../CommonUI/ActiveInactiveSwitch/ActiveInactiveSwitch"
import { DataTable } from "../../../components/DataTable"
import { useLocalPaginatedRequest } from "../../../hooks/useLocalPaginatedRequest"
import {
   DeleteTrailerCustomizationSubType,
   GetTrailerCustomizationSubTypesListing,
   UpdateTrailerCustomizationSubType,
   UpdateTrailerCustomizationPropertySequence,
   GetTrailersDetails,
} from "../../../api/api.service"
import { APP_ROUTES, getRoute } from "../../../helpers/routeHelpers"
import { SearchBox } from "../../../components/SearchBox"
import { Button } from "../../../components/Button"
import { AutoComplete } from "../../../components/AutoComplete"
import { EquipmentCategoryItemsStatus } from "../../../constants"
import { convertToSelectOptions, withOrderSaveGuard } from "../../../utils/commonUtils"
import { useParams } from "react-router-dom/cjs/react-router-dom.min"
import { convertDateTime, dateTimeFormat } from "utils/dateFormatUtils"
import ManageCustomizationCategoryItems from "./ManageCustomizationCategoryItems"
import DeleteModal from "../CommonUI/Modal/DeleteModal"
import { TOGGLE_STATUS } from "./FormConfig"
import autoCompleteStyles from "components/AutoComplete/autoCompleteStyles"
import TruncatedTextWithTooltip from "components/CustomTooltip/CustomTooltip"

const COLUMNS = {
   ITEM_NAME: "Item",
   PRICE: "Price",
   DATE_EDITED: "Date edited",
   STATUS: "Status",
   ACTIONS: "Actions",
   POPULAR_PERSONALIZATION: "Popular Personalization",
}
const DATA_KEY = "data"
const TOTAL_KEY = "total"
const CustomizationCategoryItems = ({}) => {
   const { trailerId, categoryId } = useParams()
   const history = useHistory()
   const [statusFilters, setStatusFilters] = useState(null)
   const [selection, setSelection] = useState(null)
   const [openDelModal, setOpenDelModal] = useState(false)
   // Personalization reorder is locked unless the trailer is in custom order (flag is trailer-wide).
   const [orderCustomized, setOrderCustomized] = useState(false)
   useEffect(() => {
      let cancelled = false
      GetTrailersDetails(trailerId)
         .then(res => {
            if (!cancelled) setOrderCustomized(Boolean(res?.orderCustomized))
         })
         .catch(() => {})
      return () => {
         cancelled = true
      }
   }, [trailerId])

   const {
      data,
      isFetching,
      searchText,
      handleSearchChange,
      request,
      setTableDataChangeLoading,
      tableDataChangeLoading,
      setData,
   } = useLocalPaginatedRequest({
      requestFn: GetTrailerCustomizationSubTypesListing,
      // customParams bypasses the hook's pagination. The sequence endpoint requires every
      // non-deleted option of the category in one payload, so a paginated view could only ever
      // submit a partial list; column=sequenceNo is what makes a saved drag order survive a
      // refresh instead of falling back to `id desc`.
      customParams: {
         categoryId: Number(categoryId),
         configuratorTrailerId: Number(trailerId),
         column: "sequenceNo",
         limit: -1,
         page: 1,
         ...(statusFilters?.value && { status: statusFilters?.value }),
      },
      deps: [statusFilters],
      dataKey: DATA_KEY,
      totalKey: TOTAL_KEY,
   })

   // Reorders the options inside this personalization category. The whole category is loaded
   // (see customParams above), so the payload always covers every non-deleted option as the
   // endpoint requires. Reorder is locked unless the trailer is in custom order.
   const handleDragEnd = async result => {
      if (!orderCustomized) return
      if (statusFilters?.value) return
      if (!result.destination) return
      if (result.destination.index === result.source.index) return

      const previous = Array.from(data)
      const items = Array.from(data)
      const [reorderedItem] = items.splice(result.source.index, 1)
      items.splice(result.destination.index, 0, reorderedItem)

      setData(items)

      const payload = {
         customizationSubTypeId: Number(categoryId),
         sequenceItems: items.map((item, index) => ({
            id: Number(item.id),
            sequenceNo: index,
         })),
      }

      try {
         await withOrderSaveGuard(() => UpdateTrailerCustomizationPropertySequence(payload))
         toast.success("Sequence updated successfully!")
      } catch (err) {
         // Roll back so the table does not keep an order the server rejected.
         setData(previous)
         toast.error("Failed to update sequence")
      }
   }

   const [eventsType, setEventsType] = useState({
      isManage: null,
      isUpdate: null,
      isDetails: null,
   })

   const handleInitManage = (obj = null) => {
      setEventsType(prev => ({ ...prev, isManage: true }))
      setSelection(obj)
   }

   const handleInitViewDetails = obj => {
      setSelection(obj)
      setEventsType(prev => ({ ...prev, isDetails: true, isManage: true }))
   }

   const handleSelectStatusOption = option => {
      setStatusFilters(option)
   }

   const handleStatusChange = async (currStatus, id) => {
      let status =
         currStatus === EquipmentCategoryItemsStatus.Active
            ? EquipmentCategoryItemsStatus.Inactive
            : EquipmentCategoryItemsStatus.Active
      let dataClone = [...data]
      dataClone.map(el => {
         if (el?.id == id) el.status = status
      })
      setData(dataClone)
      let payload = {
         status: status,
      }
      try {
         setTableDataChangeLoading(true)
         let res = await UpdateTrailerCustomizationSubType(id, payload)
         toast.success("Item status has been updated !")
      } catch (err) {
         toast.error(err.message)
         let dataClone = [...data]
         dataClone.map(el => {
            if (el?.id == id) el.status = currStatus
         })
         setData(dataClone)
      } finally {
         setTableDataChangeLoading(false)
      }
   }

   const handleInitDelete = obj => {
      setSelection(obj)
      setOpenDelModal(prev => !prev)
   }

   return (
      <>
         <div className="mb-3 trailer-container">
            <div className="d-flex align-items-center justify-content-between admin-inventory-management-general-inventory-responsiveness trailer-btn-container">
               <div />

               <Button
                  className={`header-button headerButtonResponsive`}
                  title="Add Item"
                  onClick={() => handleInitManage({})}
               />
            </div>
         </div>

         <div className="mb-3">
            <div className="d-flex align-items-center justify-content-between admin-inventory-management-general-inventory-responsiveness ">
               <div className="status-search-con wh-im-dpgi-status  me-2 ">
                  <AutoComplete
                     value={statusFilters}
                     isClearable
                     onChange={val => handleSelectStatusOption(val)}
                     isSearchable={false}
                     placeholder="Select status"
                     customStyles={autoCompleteStyles.STYLE_SIX}
                     classNamePrefix="status-header-search-admin"
                     options={convertToSelectOptions(
                        EquipmentCategoryItemsStatus
                     )}
                  />
               </div>
               <div className="ad-location-search-box">
                  <SearchBox
                     inputClasses="vendor-search-input input-search"
                     labelClass="d-none"
                     placeholder="Search"
                     searchIcon={true}
                     searchIconClass="custom-input-search-icon"
                     conClass={`search-input-container-class`}
                     searchText={searchText}
                     onChange={handleSearchChange}
                  />
               </div>
            </div>
         </div>
         <Row>
            <Col md={12}>
               <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="droppable">
                     {provided => (
                        <div {...provided.droppableProps} ref={provided.innerRef}>
                           <DataTable
                              isDraggable={orderCustomized && !statusFilters?.value}
                              droppablePlaceholder={provided.placeholder}
                              data={data}
                              tableClasses="customization-category-table"
                              loading={isFetching}
                              config={[
                                 {
                                    title: COLUMNS.ITEM_NAME,
                                    className: "",
                                    render: data => {
                                       return <TruncatedTextWithTooltip text={data.name} />
                                    },
                                 },
                                 {
                                    title: COLUMNS.PRICE,
                                    className: "",
                                    render: data => "$" + data?.price || "-",
                                 },
                                 {
                                    title: COLUMNS.DATE_EDITED,
                                    className: "",
                                    render: data => {
                                       return convertDateTime({
                                          date: data.updatedAt,
                                          customFormat:
                                             dateTimeFormat.casitaCustomDateFormat,
                                          dateOnly: true,
                                       })
                                    },
                                 },
                                 {
                                    title: COLUMNS.POPULAR_PERSONALIZATION,
                                    className: "",
                                    render: data =>
                                       data?.isPopular
                                          ? TOGGLE_STATUS.Yes
                                          : TOGGLE_STATUS.No,
                                 },
                                 {
                                    title: COLUMNS.STATUS,
                                    className: "",
                                    render: data => (
                                       <ActiveInactiveSwitch
                                          isDisable={tableDataChangeLoading}
                                          id={data.id}
                                          name={`${data.id}`}
                                          onChange={e =>
                                             handleStatusChange(data?.status, data?.id)
                                          }
                                          isChecked={
                                             data?.status ==
                                             EquipmentCategoryItemsStatus.Active
                                          }
                                       />
                                    ),
                                 },
            
                                 {
                                    title: COLUMNS.ACTIONS,
            
                                    render: data => (
                                       <div className="d-flex gap-2 pointer">
                                          <img
                                             src={eyeIcon}
                                             onClick={() => handleInitViewDetails(data)}
                                          />
            
                                          <img
                                             src={editIcon}
                                             onClick={() => handleInitManage(data)}
                                          />
                                          <img
                                             src={DeleteIcon}
                                             onClick={() => handleInitDelete(data)}
                                          />
                                       </div>
                                    ),
                                 },
                              ]}
                           />
                        </div>
                     )}
                  </Droppable>
               </DragDropContext>
            </Col>
         </Row>

         {Object.values(eventsType).includes(true) && (
            <ManageCustomizationCategoryItems
               isUpdating={Boolean(eventsType.isManage && selection)}
               isDetailView={eventsType.isDetails}
               isOpen={eventsType.isManage}
               id={selection?.id}
               onSuccess={request}
               onToggleModal={() => {
                  setSelection({})
                  setEventsType(prev => ({
                     ...prev,
                     isManage: false,
                     isDetails: false,
                  }))
               }}
            />
         )}

         <DeleteModal
            isOpen={openDelModal}
            toggleFunction={setOpenDelModal}
            id={selection?.id}
            apiFunction={DeleteTrailerCustomizationSubType}
            title={"Personalization Item"}
            requestFunction={request}
         />
      </>
   )
}

export default CustomizationCategoryItems
